import React, { useEffect, useMemo, useState } from "react";
import { useLazyQuery } from "@apollo/client";
import { Drawer, Radio, Table, Tag, Tooltip, Typography } from "antd";
import { LuInfo, LuLink } from "react-icons/lu";
import CustomButton from "../../commonComponents/button";
import DebounceInput from "../../commonComponents/DebounceInput";
import CustomAvatar from "../../commonComponents/CustomAvatar";
import InlineNote from "../../commonComponents/InlineNote";
import MediaLabel from "./MediaLabel";
import MediaPreview, { MEDIA_PREVIEW_TYPE } from "../../commonComponents/MediaPreview";
import { DATE_TIME_FORMATS, formatTimeInSeconds } from "../../../utils/dateTimes";
import { convertUtcToLocal } from "../../../library/utils";
import { COMMON_PAGINATION } from "../../../library/constants";
import { GET_USER_MEDIA_ENGAGEMENT, GET_ASSIGNED_OPPS_VIDEO_DETAILS } from "../../../graphql/query";
import useLeadRedirection from "../../../hooks/useLeadRedirection";
import {
  LINK_TRACKING_NOTICE,
  MEDIA_TYPE,
  MEDIA_TYPE_LABELS,
  MEDIA_DETAIL_STATUS_COLOR,
  MEDIA_DETAIL_STATUS_LABEL,
  MEDIA_TYPE_TO_API,
  MEDIA_TYPE_TO_PREVIEW_TYPE,
} from "../activityCenter/mediaTrackingHelpers";

const { Text } = Typography;

// Video-status filter options for the Leads-with-video drawer. Maps to the
// query's `hasVideo`: ALL → null, WITH → true, WITHOUT → false.
export const VIDEO_FILTER = {
  ALL: "all",
  WITH: "with",
  WITHOUT: "without",
};

const VIDEO_FILTER_OPTIONS = [
  { label: "All", value: VIDEO_FILTER.ALL },
  { label: "Video sent", value: VIDEO_FILTER.WITH },
  { label: "No video sent", value: VIDEO_FILTER.WITHOUT },
];

// Resolve the filter selection into the query's `hasVideo` (null = no filter).
const hasVideoFromFilter = filter => {
  if (filter === VIDEO_FILTER.WITH) return true;
  if (filter === VIDEO_FILTER.WITHOUT) return false;
  return null;
};

// Normalizes the getAssignedOppsVideoDetail response into the shape buildRows
// expects (lead → items[]). The new query nests one level deeper
// (lead → opportunities[] → items[]), so we flatten all of a lead's
// opportunity items into lead-level items and derive the fields buildRows reads
// (totalMedia, downloadedCount, and per-item isDownloaded from downloadCount).
// A lead with no video items keeps items: [] → the "No video sent" empty state.
const adaptAssignedOppsVideo = res => {
  if (!res) return null;
  const leads = (Array.isArray(res.leads) ? res.leads : []).map(lead => {
    const items = (Array.isArray(lead.opportunities) ? lead.opportunities : [])
      .flatMap(opp => (Array.isArray(opp.items) ? opp.items : []))
      .map(it => ({ ...it, isDownloaded: (Number(it.downloadCount) || 0) > 0 }));
    return {
      leadId: lead.leadId,
      leadName: lead.leadName,
      totalMedia: items.length,
      downloadedCount: items.filter(it => it.isDownloaded).length,
      items,
    };
  });
  return { totalLeads: res.totalLeads || 0, totalOpps: res.totalOpps || 0, leads };
};

const TYPE_LABEL_MAP = {
  [MEDIA_TYPE.IMAGE]: "images",
  [MEDIA_TYPE.VIDEO]: "videos",
  [MEDIA_TYPE.DOC]: "documents",
};

const formatDate = value =>
  value ? convertUtcToLocal(value, DATE_TIME_FORMATS.MMM_D_YYYY_hh_mm_A) : "--";

// Wraps the shared dateTimes helper but returns "" (instead of "0s") for null/zero,
// so the row cell falls back to "--" in the table.
const formatWatchTime = totalSeconds => {
  const n = Number(totalSeconds);
  if (!n || n <= 0) return "";
  return formatTimeInSeconds(n);
};

// Pulls the filename out of a media URL (strips query/hash, decodes %20 etc.).
const getFileNameFromUrl = url => {
  if (!url || typeof url !== "string") return "";
  try {
    const pathname = url.split(/[?#]/)[0];
    const segment = pathname.split("/").filter(Boolean).pop() || "";
    return decodeURIComponent(segment);
  } catch {
    return "";
  }
};

const buildChildLabel = (mediaType, index, mediaUrl) =>
  getFileNameFromUrl(mediaUrl) ||
  `${MEDIA_TYPE_LABELS[mediaType]?.singular || "Item"} ${index + 1}`;

// Builds the "Yes" / "Yes (N times)" node for a child row's Downloaded cell.
const buildDownloadedNode = (isDownloaded, count) => {
  if (!isDownloaded) return "";
  const n = Number(count) || 0;
  return (
    <>
      Yes
      {n > 1 && <span className="!text-[#52C41A] !font-normal !text-xs"> ({n} times)</span>}
    </>
  );
};

// Returns the thumbnail URL for the row preview. If no thumbnail, MediaLabel
// renders the type-specific fallback icon.
const getPreviewUrl = item => item?.thumbnail || "";

// Transforms `leads[]` from the API into Antd Table rows with child rows from `items[]`.
const buildRows = (leads, mediaType) =>
  (Array.isArray(leads) ? leads : []).map(lead => {
    const items = Array.isArray(lead?.items) ? lead.items : [];
    const hasChildren = items.length > 1;
    // Items come back ordered latest-first, so items[0] is the most recent record.
    // Parent row mirrors that latest item for sentAt/method/status/openedAt/watchTime;
    // mediaCount and downloadedCount stay as lead-level rollups.
    const firstItem = items[0] || null;
    const downloadedCount = Number(lead?.downloadedCount) || 0;
    return {
      key: `lead-${lead.leadId}`,
      leadId: lead.leadId,
      leadName: lead.leadName,
      // messageId of the latest record — appended to the activity-center link
      // when the lead name is clicked.
      messageId: firstItem?.messageId || null,
      // No media record for this lead — the Media cell shows `emptyMediaLabel`
      // (e.g. "No video sent") instead of a media preview.
      hasMedia: items.length > 0,
      mediaCount: lead.totalMedia,
      // Source URL for the click-to-preview modal.
      mediaUrl: firstItem?.mediaUrl || null,
      thumbnail: getPreviewUrl(firstItem),
      sentAt: firstItem?.sentAt || null,
      method: firstItem?.method || null,
      link: firstItem?.link || null,
      status: firstItem?.status || null,
      openedAt: firstItem?.openedAt || null,
      watchTime: formatWatchTime(firstItem?.totalPlayDuration),
      downloaded: downloadedCount > 0 ? String(downloadedCount) : "",
      children: hasChildren
        ? items.map((item, idx) => ({
            key: `item-${item.mmsObjectId ?? `${lead.leadId}-${idx}`}`,
            // Carried so the clickable child media name can open the same
            // activity-center link with this record's messageId.
            leadId: lead.leadId,
            messageId: item.messageId || null,
            label: buildChildLabel(mediaType, idx, item.mediaUrl),
            mediaUrl: item.mediaUrl || null,
            thumbnail: getPreviewUrl(item),
            sentAt: item.sentAt,
            method: item.method,
            link: item.link,
            status: item.status,
            openedAt: item.openedAt,
            watchTime: formatWatchTime(item.totalPlayDuration),
            downloaded: buildDownloadedNode(item.isDownloaded, item.downloadCount),
          }))
        : undefined,
    };
  });

const NOWRAP_CELL_PROPS = { style: { whiteSpace: "nowrap" } };
const cellNowrap = () => NOWRAP_CELL_PROPS;

const getColumns = (mediaType, redirectToLead, emptyMediaLabel, onPreview) => {
  const isChild = record => !!record?.label;

  // Open the activity center for this row, appending its messageId when present
  // (empty message_id is dropped by useLeadRedirection's appendParams).
  const goToLead = record => {
    if (!record?.leadId) return;
    redirectToLead?.(record.leadId, { message_id: record.messageId });
  };

  // Media cell — the thumbnail always opens the preview. The name/label opens
  // the activity center, EXCEPT a root row with multiple media, where the
  // "N videos" label opens the preview carousel instead.
  const mediaCell = record => {
    const child = isChild(record);
    const rootMultiple = !child && Number(record?.mediaCount) > 1;
    const onLabelClick = rootMultiple
      ? () => onPreview?.(record)
      : record?.leadId
      ? () => goToLead(record)
      : undefined;
    return (
      <MediaLabel
        type={mediaType}
        thumbnail={record.thumbnail}
        label={child ? record.label : undefined}
        count={record.mediaCount}
        onThumbnailClick={record?.mediaUrl ? () => onPreview?.(record) : undefined}
        onLabelClick={onLabelClick}
      />
    );
  };

  const baseColumns = [
    {
      title: "Lead name",
      width: 200,
      ellipsis: { showTitle: true },
      onCell: cellNowrap,
      render: (_, record) => {
        if (isChild(record)) return null;
        return (
          <span className="flex items-center gap-2 whitespace-nowrap">
            <CustomAvatar name={record.leadName} bgClass="bg-grey-700" />
            <span
              title="Open in activity center"
              className="text-twLinkColor !text-sm ellipsis cursor-pointer"
              onClick={() => goToLead(record)}
            >
              {record.leadName}
            </span>
          </span>
        );
      },
    },
    { title: "", width: 48 },
    {
      title: "Media",
      width: 200,
      onCell: cellNowrap,
      render: (_, record) => {
        if (isChild(record)) {
          return mediaCell(record);
        }
        // Lead with no media record → show the empty label (e.g. "No video sent").
        if (!record.hasMedia && emptyMediaLabel) {
          return <Text className="!text-sm text-black-45">{emptyMediaLabel}</Text>;
        }
        return mediaCell(record);
      },
    },
    {
      title: "Sent at",
      width: 200,
      onCell: cellNowrap,
      render: (_, record) => formatDate(record.sentAt),
    },
    {
      title: (
        <span className="inline-flex items-center gap-1">
          Method
          <Tooltip title={LINK_TRACKING_NOTICE} overlayStyle={{ maxWidth: 320 }}>
            <LuInfo className="text-black-45 cursor-help text-xs" />
          </Tooltip>
        </span>
      ),
      width: 110,
      onCell: cellNowrap,
      render: (_, record) => {
        if (!record.method) return <Text className="!text-sm">--</Text>;
        if (record.method === "LINK") {
          return (
            <CustomButton
              type="link"
              btnText="Link"
              icon={<LuLink />}
              size="small"
              className="!p-0 !h-auto"
              disabled={!record.link}
              handleClick={() => {
                if (record.link) window.open(record.link, "_blank", "noopener,noreferrer");
              }}
            />
          );
        }
        return <Text className="!text-sm">MMS</Text>;
      },
    },
    {
      title: "Status",
      width: 110,
      onCell: cellNowrap,
      render: (_, record) => {
        const { status } = record;
        if (!status) return null;
        return (
          <Tag color={MEDIA_DETAIL_STATUS_COLOR[status]}>
            {MEDIA_DETAIL_STATUS_LABEL[status] || status}
          </Tag>
        );
      },
    },
  ];

  baseColumns.push({
    title: "Downloaded",
    width: 130,
    onCell: cellNowrap,
    render: (_, record) => (
      <Text className={`!text-sm ${record.downloaded ? "!text-[#52C41A]" : ""}`}>
        {record.downloaded || "--"}
      </Text>
    ),
  });

  baseColumns.push({
    title: "Opened at",
    width: 200,
    onCell: cellNowrap,
    render: (_, record) => formatDate(record.openedAt),
  });

  if (mediaType === MEDIA_TYPE.VIDEO) {
    baseColumns.push({
      title: "Watch time",
      width: 140,
      onCell: cellNowrap,
      render: (_, record) => {
        if (!record.watchTime) return <Text className="!text-sm">--</Text>;
        return <p className="text-black-88 text-sm whitespace-nowrap">{record.watchTime}</p>;
      },
    });
  }

  return baseColumns;
};

const MessageTypeDetailDrawer = ({
  open,
  onClose,
  mediaType,
  userId,
  userName,
  count,
  filterLabel,
  filterType,
  dateRange,
  opportunityTypeId,
  // Media-column text for leads with no record (e.g. "No video sent"). Omit for
  // the standard media drill-downs.
  emptyMediaLabel,
  // Show the "video sent" / "no video sent" status filter above the table
  // (Leads with video drill-down only).
  showVideoStatusFilter = false,
  // Optional subtitle override. Default reads "{count} {typeLabel} sent";
  // the Leads-with-video drawer passes its own (count = opps assigned, not
  // videos sent, so the default would be misleading).
  subtitle,
}) => {
  const [params, setParams] = useState({ page: 1, pageSize: 10 });
  const [search, setSearch] = useState("");
  // Status filter for the Leads-with-video drawer (VIDEO_FILTER enum).
  const [videoFilter, setVideoFilter] = useState(VIDEO_FILTER.ALL);
  // Click-to-preview via the shared MediaPreview (single item or carousel).
  const [preview, setPreview] = useState({ open: false, items: [] });

  const openPreview = record => {
    if (!record) return;
    const type = MEDIA_TYPE_TO_PREVIEW_TYPE[mediaType] || MEDIA_PREVIEW_TYPE.IMAGE;
    const toItem = r => ({ url: r?.mediaUrl, type, name: r?.label });
    // Root row with multiple media → preview all of the lead's items in a
    // carousel; a single media (child row, or a lead with one) → just that one.
    const kids = Array.isArray(record.children) ? record.children.filter(c => c?.mediaUrl) : [];
    const items = kids.length ? kids.map(toItem) : [toItem(record)];
    if (!items.some(i => i.url)) return;
    setPreview({ open: true, items });
  };

  const { redirectToLead } = useLeadRedirection();

  const [fetchEngagement, { data, loading }] = useLazyQuery(GET_USER_MEDIA_ENGAGEMENT, {
    fetchPolicy: "network-only",
  });
  // Leads-with-video drill-down uses a different query (assigned opps + video
  // status). `hasVideo`: null = all, true = video sent, false = no video sent.
  const [fetchVideoOpps, { data: videoData, loading: videoLoading }] = useLazyQuery(
    GET_ASSIGNED_OPPS_VIDEO_DETAILS,
    { fetchPolicy: "network-only" }
  );
  const hasVideo = hasVideoFromFilter(videoFilter);

  useEffect(() => {
    if (!open || !userId || !mediaType) return;
    if (showVideoStatusFilter) {
      fetchVideoOpps({
        variables: {
          userId: Number(userId),
          filterType,
          dateRange,
          opportunityTypeId: opportunityTypeId || undefined,
          ...(hasVideo === null ? {} : { hasVideo }),
          page: params.page,
          pageSize: params.pageSize,
          search: search || undefined,
        },
      });
      return;
    }
    fetchEngagement({
      variables: {
        userId: Number(userId),
        mediaType: MEDIA_TYPE_TO_API[mediaType],
        filterType,
        dateRange,
        opportunityTypeId: opportunityTypeId || undefined,
        page: params.page,
        pageSize: params.pageSize,
        search: search || undefined,
      },
    });
  }, [
    open,
    userId,
    mediaType,
    filterType,
    dateRange,
    opportunityTypeId,
    params.page,
    params.pageSize,
    search,
    showVideoStatusFilter,
    hasVideo,
    fetchEngagement,
    fetchVideoOpps,
  ]);

  // Reset to page 1 each time the drawer is reopened or search changes
  useEffect(() => {
    if (open) {
      setParams({ page: 1, pageSize: 10 });
      setSearch("");
      setVideoFilter(VIDEO_FILTER.ALL);
    }
  }, [open, userId, mediaType]);

  // When search or the video-status filter changes, reset to first page.
  useEffect(() => {
    setParams(prev => ({ ...prev, page: 1 }));
  }, [search, videoFilter]);

  // Leads-with-video uses the assigned-opps query (adapted to the engagement
  // shape); everything else uses the media-engagement query directly.
  const engagement = showVideoStatusFilter
    ? adaptAssignedOppsVideo(videoData?.getAssignedOppsVideoDetail)
    : data?.getUserMediaEngagement;
  const isLoading = showVideoStatusFilter ? videoLoading : loading;
  const rows = useMemo(() => buildRows(engagement?.leads, mediaType), [engagement, mediaType]);
  const total = engagement?.totalLeads || 0;
  const columns = useMemo(
    () => getColumns(mediaType, redirectToLead, emptyMediaLabel, openPreview),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [mediaType, redirectToLead, emptyMediaLabel]
  );
  const typeLabel = TYPE_LABEL_MAP[mediaType] || "items";
  // `subtitle` (when provided) is a full sentence incl. the period; otherwise
  // fall back to the default "{count} {type} sent in {filterLabel}".
  const subtitleText = subtitle || `${count} ${typeLabel} sent in ${filterLabel}`;

  return (
    <Drawer
      title={
        <div className="flex flex-col">
          <Text className="!text-black-88 !text-base !font-semibold">{userName}</Text>
          <Text className="!text-black-45 !font-normal !text-sm">{subtitleText}</Text>
        </div>
      }
      width={1400}
      closable
      onClose={onClose}
      open={open}
      styles={{ body: { padding: "12px 16px" } }}
    >
      {showVideoStatusFilter && (
        <InlineNote className="mb-3">
          The count here may differ from the report. A single lead can have more than one
          opportunity — the report counts opportunities, while this list is grouped by lead (one row
          per lead).
        </InlineNote>
      )}
      <div className="mb-3 flex flex-wrap items-center gap-4">
        <DebounceInput
          placeholder="Search by lead name"
          value={search}
          onChange={setSearch}
          allowClear
          inputWidthClass="!w-72"
        />
        {showVideoStatusFilter && (
          <Radio.Group
            optionType="button"
            options={VIDEO_FILTER_OPTIONS}
            value={videoFilter}
            onChange={e => setVideoFilter(e.target.value)}
          />
        )}
      </div>
      <Table
        bordered={false}
        loading={isLoading}
        dataSource={rows}
        columns={columns}
        size="small"
        tableLayout="fixed"
        scroll={{ x: "max-content", y: "calc(100vh - 240px)" }}
        expandable={{
          expandIconColumnIndex: 1,
        }}
        pagination={{
          current: params.page,
          pageSize: params.pageSize,
          total,
          size: "small",
          showSizeChanger: true,
          pageSizeOptions: COMMON_PAGINATION.PAGE_SIZE_OPTIONS,
          position: ["bottomLeft"],
          showTotal: (t, range) => `${range[0]}-${range[1]} of ${t} leads`,
          onChange: (page, pageSize) => setParams({ page, pageSize }),
        }}
      />

      {/* Click-to-preview — single media or a carousel of the lead's media. */}
      <MediaPreview
        open={preview.open}
        items={preview.items}
        title={userName}
        showDownload={false}
        onClose={() => setPreview({ open: false, items: [] })}
      />
    </Drawer>
  );
};

export default MessageTypeDetailDrawer;
